import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Shield, Eye, EyeOff, LogOut, Inbox, AlertTriangle, X } from "lucide-react";
import { useAdmin } from "../context/AdminContext";
import { MC_SAVE_FAILED_EVENT } from "./useManagedCollection";

/**
 * Global admin banner — shown on every public page while logged in as admin.
 * It tells the admin that editing is on, lets them toggle a public "Preview",
 * jump to the Submissions dashboard, and exit. The banner is only rendered for
 * authenticated admins; logged-out / regular users never see it.
 */
export default function AdminBanner() {
  const { isAdmin, adminMode, toggleAdminMode, logout } = useAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  // Save failures surfaced by useManagedCollection. Previously a write that
  // never reached the database was only console.error'd, so the admin saw the
  // optimistic card and assumed it had saved — the edit then existed nowhere
  // but their own browser. Show it.
  const [saveError, setSaveError] = useState<{ name: string; message: string } | null>(null);

  useEffect(() => {
    const onFail = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      setSaveError({ name: d.name || "This card", message: d.message || "Unknown error" });
    };
    window.addEventListener(MC_SAVE_FAILED_EVENT, onFail as EventListener);
    return () => window.removeEventListener(MC_SAVE_FAILED_EVENT, onFail as EventListener);
  }, []);

  if (!isAdmin) return null;
  // The standalone submissions dashboard has its own chrome.
  if (location.pathname === "/admin" || location.pathname === "/admin/login") return null;

  return (
    <>
      {saveError && (
        <div
          data-mc-no-drag
          className="fixed bottom-4 right-4 z-[10001] max-w-sm rounded-lg bg-red-600 text-white shadow-2xl"
        >
          <div className="flex items-start gap-2 p-3">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-semibold leading-snug">
                Not saved — “{saveError.name}” was not written to the site.
              </p>
              <p className="text-[11px] text-red-100 leading-snug mt-0.5 break-words">
                {saveError.message}
              </p>
              <p className="text-[11px] text-red-100 leading-snug mt-1">
                The card still shows its last saved values. Please try again.
              </p>
            </div>
            <button
              onClick={() => setSaveError(null)}
              className="shrink-0 rounded p-0.5 hover:bg-white/20 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    <div className="sticky top-0 z-[9997] bg-[#082f6d] text-white shadow-md">
      <div className="page-container h-10 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Shield className="w-4 h-4 text-[#f59e0b] shrink-0" />
          <span className="text-[12px] sm:text-[13px] font-semibold truncate">
            Admin mode{" "}
            <span className="font-normal text-blue-200 hidden sm:inline">
              — {adminMode ? "editing controls are on across the site" : "previewing as a public visitor"}
            </span>
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-[11px] font-medium px-2.5 py-1.5 rounded-md transition-colors"
            title="Manage form submissions"
          >
            <Inbox className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Submissions</span>
          </button>
          <button
            onClick={toggleAdminMode}
            className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-[11px] font-medium px-2.5 py-1.5 rounded-md transition-colors"
            title="Toggle editing controls"
          >
            {adminMode ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">{adminMode ? "Preview" : "Edit"}</span>
          </button>
          <button
            onClick={() => {
              logout();
              navigate("/");
            }}
            className="flex items-center gap-1.5 bg-red-500/25 hover:bg-red-500/40 text-red-100 text-[11px] font-medium px-2.5 py-1.5 rounded-md transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Exit</span>
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
